import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertCircle,
  ArrowUpRight,
  Bell,
  CheckCircle2,
  Clock3,
  FileEdit,
  MessageSquareText,
  Music2,
  Radio,
  RefreshCw,
  Send,
  Smartphone,
  Sparkles,
  Users,
  XCircle,
} from "lucide-react";
import { AdminDashboardData } from "../../types/admin";
import { fetchDashboard } from "./dashboardApi";
import styles from "./Dashboard.module.css";

type Accent = "blue" | "violet" | "orange" | "green" | "cyan";

interface MetricCardProps {
  accent: Accent;
  href: string;
  icon: React.ElementType;
  label: string;
  meta: string;
  value: number;
}

const numberFormatter = new Intl.NumberFormat("tr-TR");

function formatNumber(value?: number) {
  return numberFormatter.format(value ?? 0);
}

function getPercentage(value: number, total: number) {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((value / total) * 100));
}

function MetricCard({
  accent,
  href,
  icon: Icon,
  label,
  meta,
  value,
}: MetricCardProps) {
  return (
    <Link
      to={href}
      className={`${styles.metricCard} ${styles[`accent${accent}`]}`}
    >
      <div className={styles.metricTop}>
        <span className={styles.metricIcon}>
          <Icon size={20} strokeWidth={2.2} />
        </span>
        <ArrowUpRight size={18} className={styles.metricArrow} />
      </div>
      <strong className={styles.metricValue}>{formatNumber(value)}</strong>
      <span className={styles.metricLabel}>{label}</span>
      <span className={styles.metricMeta}>{meta}</span>
    </Link>
  );
}

export default function DashboardPage() {
  const [dashboard, setDashboard] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadDashboard = useCallback(async (isRefresh = false) => {
    try {
      isRefresh ? setRefreshing(true) : setLoading(true);
      setError("");
      const data = await fetchDashboard();
      setDashboard(data);
      setLastUpdated(new Date());
    } catch (err: any) {
      setError(err?.message || "Dashboard verileri yüklenemedi.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const insights = useMemo(() => {
    if (!dashboard) {
      return {
        activeUsers: 0,
        publishedSongs: 0,
        completedRequests: 0,
        enabledDevices: 0,
        deliverySuccess: 0,
      };
    }

    const notificationAttempts =
      dashboard.notifications.total_sent + dashboard.notifications.failed;

    return {
      activeUsers: getPercentage(
        dashboard.users.active,
        dashboard.users.total
      ),
      publishedSongs: getPercentage(
        dashboard.songs.published,
        dashboard.songs.total
      ),
      completedRequests: getPercentage(
        dashboard.song_requests.completed,
        dashboard.song_requests.total
      ),
      enabledDevices: getPercentage(
        dashboard.devices.notification_enabled,
        dashboard.devices.registered
      ),
      deliverySuccess: getPercentage(
        dashboard.notifications.total_sent,
        notificationAttempts
      ),
    };
  }, [dashboard]);

  if (loading) {
    return (
      <div className={styles.statePage} aria-live="polite">
        <div className={styles.loadingOrb}>
          <Activity size={30} />
        </div>
        <h2>Kontrol merkezi hazırlanıyor</h2>
        <p>Güncel operasyon verileri Drumly API'den alınıyor.</p>
        <div className={styles.skeletonGrid} aria-hidden="true">
          {Array.from({ length: 5 }).map((_, index) => (
            <span key={index} className={styles.skeletonCard} />
          ))}
        </div>
      </div>
    );
  }

  if (error && !dashboard) {
    return (
      <div className={styles.statePage}>
        <div className={`${styles.loadingOrb} ${styles.errorOrb}`}>
          <AlertCircle size={30} />
        </div>
        <h2>Dashboard'a ulaşılamadı</h2>
        <p>{error}</p>
        <button
          type="button"
          onClick={() => loadDashboard()}
          className={styles.primaryButton}
        >
          <RefreshCw size={17} />
          Tekrar dene
        </button>
      </div>
    );
  }

  if (!dashboard) return null;

  const openRequests =
    dashboard.song_requests.pending + dashboard.song_requests.in_progress;
  const archivedSongs = dashboard.songs.archived ?? 0;

  return (
    <div className={styles.dashboard}>
      <header className={styles.hero}>
        <div className={styles.heroGlow} aria-hidden="true" />
        <div className={styles.heroContent}>
          <div className={styles.eyebrow}>
            <span className={styles.liveDot} />
            Drumly yönetim merkezi
          </div>
          <h1>
            Günaydın, <span>ritim sende.</span>
          </h1>
          <p>
            Platformun nabzını tek ekrandan izle, dikkat isteyen alanlara
            hızlıca ulaş.
          </p>
        </div>

        <div className={styles.heroActions}>
          <div className={styles.updatedAt}>
            <span>Son güncelleme</span>
            <strong>
              {lastUpdated
                ? lastUpdated.toLocaleTimeString("tr-TR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "—"}
            </strong>
          </div>
          <button
            type="button"
            onClick={() => loadDashboard(true)}
            className={styles.refreshButton}
            disabled={refreshing}
          >
            <RefreshCw
              size={17}
              className={refreshing ? styles.spinning : undefined}
            />
            {refreshing ? "Güncelleniyor" : "Yenile"}
          </button>
        </div>
      </header>

      {error && (
        <div className={styles.inlineError} role="alert">
          <AlertCircle size={18} />
          <span>{error} Son başarılı veriler gösteriliyor.</span>
          <button type="button" onClick={() => loadDashboard(true)}>
            Yeniden dene
          </button>
        </div>
      )}

      <section className={styles.metricsGrid} aria-label="Ana metrikler">
        <MetricCard
          accent="blue"
          href="/users"
          icon={Users}
          label="Toplam kullanıcı"
          value={dashboard.users.total}
          meta={`Son 7 günde +${formatNumber(
            dashboard.users.new_last_7_days
          )}`}
        />
        <MetricCard
          accent="violet"
          href="/songs"
          icon={Music2}
          label="Şarkı arşivi"
          value={dashboard.songs.total}
          meta={`${formatNumber(dashboard.songs.published)} yayında`}
        />
        <MetricCard
          accent="orange"
          href="/song-requests"
          icon={MessageSquareText}
          label="Açık talep"
          value={openRequests}
          meta={`${formatNumber(dashboard.song_requests.total)} toplam talep`}
        />
        <MetricCard
          accent="green"
          href="/notifications"
          icon={Send}
          label="Gönderilen bildirim"
          value={dashboard.notifications.total_sent}
          meta={`Son 7 günde ${formatNumber(
            dashboard.notifications.sent_last_7_days
          )}`}
        />
        <MetricCard
          accent="cyan"
          href="/users"
          icon={Smartphone}
          label="Kayıtlı cihaz"
          value={dashboard.devices.registered}
          meta={`${formatNumber(
            dashboard.devices.notification_enabled
          )} bildirime açık`}
        />
      </section>

      <div className={styles.contentGrid}>
        <section className={`${styles.panel} ${styles.performancePanel}`}>
          <div className={styles.panelHeader}>
            <div>
              <span className={styles.sectionKicker}>Performans</span>
              <h2>Platform sağlığı</h2>
            </div>
            <span className={styles.statusBadge}>
              <Radio size={14} />
              Canlı veriler
            </span>
          </div>

          <div className={styles.progressList}>
            <div className={styles.progressItem}>
              <div className={styles.progressHeading}>
                <span>
                  <Users size={17} /> Aktif kullanıcı oranı
                </span>
                <strong>%{insights.activeUsers}</strong>
              </div>
              <div className={styles.progressTrack}>
                <span
                  className={styles.blueProgress}
                  style={{ width: `${insights.activeUsers}%` }}
                />
              </div>
              <small>
                {formatNumber(dashboard.users.active)} aktif /{" "}
                {formatNumber(dashboard.users.total)} toplam
              </small>
            </div>

            <div className={styles.progressItem}>
              <div className={styles.progressHeading}>
                <span>
                  <Music2 size={17} /> Yayındaki şarkılar
                </span>
                <strong>%{insights.publishedSongs}</strong>
              </div>
              <div className={styles.progressTrack}>
                <span
                  className={styles.violetProgress}
                  style={{ width: `${insights.publishedSongs}%` }}
                />
              </div>
              <small>
                {formatNumber(dashboard.songs.published)} yayın /{" "}
                {formatNumber(dashboard.songs.total)} toplam
              </small>
            </div>

            <div className={styles.progressItem}>
              <div className={styles.progressHeading}>
                <span>
                  <CheckCircle2 size={17} /> Tamamlanan talepler
                </span>
                <strong>%{insights.completedRequests}</strong>
              </div>
              <div className={styles.progressTrack}>
                <span
                  className={styles.greenProgress}
                  style={{ width: `${insights.completedRequests}%` }}
                />
              </div>
              <small>
                {formatNumber(dashboard.song_requests.completed)} tamamlandı /{" "}
                {formatNumber(dashboard.song_requests.total)} toplam
              </small>
            </div>

            <div className={styles.progressItem}>
              <div className={styles.progressHeading}>
                <span>
                  <Bell size={17} /> Bildirime açık cihazlar
                </span>
                <strong>%{insights.enabledDevices}</strong>
              </div>
              <div className={styles.progressTrack}>
                <span
                  className={styles.cyanProgress}
                  style={{ width: `${insights.enabledDevices}%` }}
                />
              </div>
              <small>
                {formatNumber(dashboard.devices.notification_enabled)} açık /{" "}
                {formatNumber(dashboard.devices.registered)} kayıtlı
              </small>
            </div>
          </div>
        </section>

        <section className={`${styles.panel} ${styles.requestPanel}`}>
          <div className={styles.panelHeader}>
            <div>
              <span className={styles.sectionKicker}>İş akışı</span>
              <h2>Şarkı talepleri</h2>
            </div>
            <Link to="/song-requests" className={styles.textLink}>
              Tümünü gör <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className={styles.donutArea}>
            <div
              className={styles.donut}
              style={
                {
                  "--complete": `${insights.completedRequests * 3.6}deg`,
                } as React.CSSProperties
              }
            >
              <div>
                <strong>{formatNumber(dashboard.song_requests.total)}</strong>
                <span>toplam</span>
              </div>
            </div>
            <div className={styles.requestLegend}>
              <div>
                <span className={styles.pendingDot} />
                <p>Bekliyor</p>
                <strong>{formatNumber(dashboard.song_requests.pending)}</strong>
              </div>
              <div>
                <span className={styles.progressDot} />
                <p>İşlemde</p>
                <strong>
                  {formatNumber(dashboard.song_requests.in_progress)}
                </strong>
              </div>
              <div>
                <span className={styles.completeDot} />
                <p>Tamamlandı</p>
                <strong>
                  {formatNumber(dashboard.song_requests.completed)}
                </strong>
              </div>
              <div>
                <span className={styles.rejectedDot} />
                <p>Reddedildi</p>
                <strong>
                  {formatNumber(dashboard.song_requests.rejected)}
                </strong>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className={styles.bottomGrid}>
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <span className={styles.sectionKicker}>İçerik</span>
              <h2>Şarkı durumu</h2>
            </div>
            <Link to="/songs" className={styles.iconLink} aria-label="Şarkılara git">
              <ArrowUpRight size={18} />
            </Link>
          </div>
          <div className={styles.compactStats}>
            <div>
              <span className={styles.publishedIcon}>
                <CheckCircle2 size={18} />
              </span>
              <p>Yayında</p>
              <strong>{formatNumber(dashboard.songs.published)}</strong>
            </div>
            <div>
              <span className={styles.draftIcon}>
                <FileEdit size={18} />
              </span>
              <p>Taslak</p>
              <strong>{formatNumber(dashboard.songs.draft)}</strong>
            </div>
            <div>
              <span className={styles.archiveIcon}>
                <Clock3 size={18} />
              </span>
              <p>Arşivde</p>
              <strong>{formatNumber(archivedSongs)}</strong>
            </div>
          </div>
        </section>

        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <span className={styles.sectionKicker}>Büyüme</span>
              <h2>Yeni kullanıcılar</h2>
            </div>
            <span className={styles.sparkleIcon}>
              <Sparkles size={18} />
            </span>
          </div>
          <div className={styles.growthStats}>
            <div>
              <span>Son 7 gün</span>
              <strong>+{formatNumber(dashboard.users.new_last_7_days)}</strong>
            </div>
            <div className={styles.growthDivider} />
            <div>
              <span>Son 30 gün</span>
              <strong>+{formatNumber(dashboard.users.new_last_30_days)}</strong>
            </div>
          </div>
          <div className={styles.growthFooter}>
            <Activity size={16} />
            <span>
              Toplam {formatNumber(dashboard.users.total)} kullanıcıdan{" "}
              {formatNumber(dashboard.users.active)} tanesi aktif.
            </span>
          </div>
        </section>

        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <span className={styles.sectionKicker}>Dağıtım</span>
              <h2>Bildirim performansı</h2>
            </div>
            <Link
              to="/notifications"
              className={styles.iconLink}
              aria-label="Bildirimlere git"
            >
              <ArrowUpRight size={18} />
            </Link>
          </div>
          <div className={styles.deliveryScore}>
            <strong>%{insights.deliverySuccess}</strong>
            <div>
              <span>Başarı oranı</span>
              <p>
                Son 7 günde{" "}
                {formatNumber(dashboard.notifications.sent_last_7_days)} gönderim
              </p>
            </div>
          </div>
          <div className={styles.deliveryFooter}>
            <span>
              <CheckCircle2 size={15} />
              {formatNumber(dashboard.notifications.total_sent)} başarılı
            </span>
            <span className={styles.failedText}>
              <XCircle size={15} />
              {formatNumber(dashboard.notifications.failed)} başarısız
            </span>
          </div>
        </section>
      </div>
    </div>
  );
}
